import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '../Portal/Portal';
import './MultiStepForm.scss';
import closeIcon from '../../../src/assets/icons/close-icon.svg';


interface StepComponentProps {
  onNext: (data: Record<string, any>) => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  initialData?: Record<string, any>;
  setStepValidity?: (isValid: boolean) => void;
}

export interface Step {
  id: string;
  icon: { src: string | ((isReached: boolean) => string); alt?: string; };
  title: string;
  isCompleted?: boolean;
  Component: React.ReactElement<StepComponentProps>;
}

interface MultiStepFormProps {
  steps: Step[];
  initialStep?: number;
  initialFormData?: Record<string, any>;
  onClose: () => void;
  onComplete: (formData: Record<string, any>) => Promise<void>;
  variant?: 'event' | 'appointment';
  isSubmitting?: boolean;
}

export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  steps,
  initialStep = 0,
  initialFormData = {},
  onClose,
  onComplete,
  variant,
  isSubmitting: externalIsSubmitting = false,
}) => {

  useEffect(() => {
    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('no-scroll');
  
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, []);

  const { t } = useTranslation('common');
  const [currentStep, setCurrentStep] = useState(initialStep);

  const [formData, setFormData] = useState<Record<string, any>>(initialFormData);
  const [isCurrentStepValid, setIsCurrentStepValid] = useState(true); // Default to true to allow navigation
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
  const isSubmitting = externalIsSubmitting || isLocalSubmitting;

  const handleNext = (stepData: Record<string, any>) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);

    if (currentStep === steps.length - 1) {
      // On last step, pass all form data to onComplete
      onComplete?.(updatedData);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only submit the form if the current step is valid
    if (isCurrentStepValid) {
      const form = document.getElementById('current-step-form') as HTMLFormElement;
      if (form) {
        if (currentStep === steps.length - 1) {
          setIsLocalSubmitting(true);
          try {
            await form.requestSubmit();
          } catch (error) {
            console.error('Form submission error:', error);
          }
          // Note: We don't set isLocalSubmitting to false here because the parent component
          // will handle the submission state through the externalIsSubmitting prop
        } else {
          form.requestSubmit();
        }
      } else {
        // If form not found, try to proceed anyway
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      }
    } else {
      console.warn('MultiStepForm - Step not valid, cannot proceed');
    }
  };

  return (
    <Portal>
      <div className="multi-step-form__overlay">
        <div className={`multi-step-form multi-step-form--steps-${steps.length} ${variant}-variant`}>
          <button className="multi-step-form__close" onClick={onClose}>
            <img src={closeIcon} alt="Close" />
          </button>
          
          <div className="multi-step-form__progress">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`multi-step-form__step ${
                  index === currentStep ? 'active' : ''
                } ${step.isCompleted || index < currentStep ? 'completed' : ''}`}
              >
                <div className="multi-step-form__step-icon">
                  <img 
                    src={typeof step.icon.src === 'function' ? step.icon.src(index <= currentStep) : step.icon.src}
                    alt={step.icon.alt || step.title}
                  />
                </div>
                <div className="multi-step-form__step-title">{step.title}</div>
                {index < steps.length - 1 && (
                  <div className="multi-step-form__step-connector" />
                )}
              </div>
            ))}
          </div>

          <div className="multi-step-form__content">
            {React.cloneElement(steps[currentStep].Component, {
              onNext: handleNext,
              onPrevious: handlePrevious,
              isFirstStep: currentStep === 0,
              initialData: formData,
              setStepValidity: setIsCurrentStepValid
            })}
          </div>
          
          <div className="multi-step-form__footer">
            <div className="form-actions">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className={`form-button form-button--previous ${currentStep === 1 ? 'disabled' : ''}`}
                  disabled={currentStep === 1}
                >
                  {t('form.buttons.previous')}
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                className="form-button form-button--next"
                disabled={!isCurrentStepValid || isSubmitting}
              >
                {currentStep === steps.length - 1
                  ? (isSubmitting ? t('form.buttons.confirming') : t('form.buttons.complete'))
                  : t('form.buttons.next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};
